export interface SidebarItem {
  key: string;
  label: string;
  /** Optional compact label for the narrow sidebar (defaults to `label`). */
  shortLabel?: string;
  icon: string;
  route?: string;
  queryParams?: Record<string, string>;
  module?: string; // Parent module (must match API feature_key)
  /** Legacy RBAC parents where this item may be granted as a nested feature. */
  permissionParents?: string[];
  /** When true, an active parent module grants this item even without its own key. */
  allowParentModule?: boolean;
  children?: SidebarItem[];
  auth?: 'public' | 'private';
}

/** Pages that are in the new IA but do not have a dedicated screen yet. */
export const COMING_SOON_PAGES: { path: string; title: string; module?: string }[] = [
  { path: 'customer-360', title: 'Customer 360' },
  { path: 'bots', title: 'Bots', module: 'bots' },
  { path: 'bot-builder', title: 'Bot Builder', module: 'bot_builder' },
  { path: 'bot-deployments', title: 'Deployments', module: 'bot_deployments' },
  { path: 'bot-conversations', title: 'Bot Conversations', module: 'bot_conversations' },
  { path: 'bot-analytics', title: 'Bot Analytics', module: 'bot_analytics' },
];

/** Query-param section → role module key for Customer Intelligence. */
export const CI_SECTION_MODULES: Record<string, string> = {
  overview: 'ci_overview',
  churn: 'ci_churn_retention',
  nba: 'ci_next_best_action',
  migration: 'ci_cohort_migration',
  rfm: 'ci_rfm_engagement',
  spend: 'ci_spend_commerce',
  loyalty: 'ci_loyalty_sentiment',
  roster: 'ci_audience_roster',
};

export const CI_PERMISSION_PARENTS = ['customer_intelligence'];

/** Integrations category id → role module key. */
export const INTEGRATION_CATEGORY_MODULES: Record<string, string> = {
  all: 'integrations_all',
  conversational: 'integrations_conversational',
  marketing: 'integrations_marketing',
  contact_center: 'integrations_contact_center',
  crm: 'integrations_crm',
  payments: 'integrations_payments',
  ecommerce: 'integrations_ecommerce',
  forms: 'integrations_forms',
  llm: 'integrations_llm',
  live_chat: 'integrations_live_chat',
};

export const INTEGRATION_PERMISSION_PARENTS = ['integrations'];
export const AI_BOT_PERMISSION_PARENTS = ['ai_bot_platform'];

const reportItem = (
  key: string,
  label: string,
  icon: string,
  type: string,
): SidebarItem => ({
  key,
  label,
  icon,
  route: '/main/report',
  queryParams: { type },
  module: 'report',
});

const billingItem = (
  key: string,
  label: string,
  icon: string,
  tab: string,
): SidebarItem => ({
  key,
  label,
  icon,
  route: '/main/billing-subscription',
  queryParams: { tab },
  module: 'billing_subscription',
});

const customerIntelligenceSection = (
  key: string,
  label: string,
  icon: string,
  section: string,
): SidebarItem => ({
  key,
  label,
  icon,
  route: '/main/customer-intelligence',
  queryParams: { section },
  module: key,
  permissionParents: CI_PERMISSION_PARENTS,
  allowParentModule: true,
});

export const SIDEBAR_CONFIG: SidebarItem[] = [
  /* ===================== HOME ===================== */
  {
    key: 'home',
    label: 'Dashboard',
    icon: 'fas fa-home',
    route: '/main/home',
    module: 'home',
  },

  /* ===================== CRM ===================== */
  {
    key: 'crm',
    label: 'CRM',
    icon: 'fas fa-address-card',
    children: [
      {
        key: 'contacts',
        label: 'Contacts',
        icon: 'fas fa-address-book',
        route: '/main/contacts',
        module: 'contacts',
      },
      {
        key: 'lead',
        label: 'Leads',
        icon: 'fas fa-envelope-open-text',
        route: '/main/leads',
        module: 'lead',
      },
      {
        key: 'account_management',
        label: 'Accounts',
        icon: 'fas fa-building',
        route: '/main/account',
        module: 'account_management',
      },
      {
        key: 'deals',
        label: 'Deals',
        icon: 'fas fa-handshake',
        route: '/main/deals',
        module: 'deals',
      },
      {
        module: 'form_builder',
        key: 'form_builder',
        label: 'Dynamic Forms',
        icon: 'fas fa-layer-group',
        route: '/main/configuration-form-builder',
        permissionParents: ['setting'],
      },
    ],
  },

  /* ===================== SALES ===================== */
  {
    key: 'sales',
    label: 'Sales',
    icon: 'fas fa-dollar-sign',
    children: [
      {
        key: 'quotes',
        label: 'Quotes',
        icon: 'fas fa-quote-left',
        route: '/main/quotes',
        module: 'quotes',
      },
      {
        key: 'invoice',
        label: 'Invoices',
        icon: 'fas fa-file-invoice',
        route: '/main/invoices',
        module: 'invoice',
      },
      {
        key: 'forecast',
        label: 'Forecast',
        icon: 'fas fa-chart-line',
        route: '/main/forecast',
        module: 'forecast',
      },
    ],
  },

  /* ===================== MARKETING ===================== */
  {
    key: 'marketing',
    label: 'Marketing',
    icon: 'fas fa-bullhorn',
    children: [
      {
        key: 'campaign',
        label: 'Campaigns',
        icon: 'fas fa-bullhorn',
        route: '/main/broadcast-campaigns',
        module: 'campaign',
      },
      {
        key: 'sequence',
        label: 'Sequences',
        icon: 'fas fa-link',
        route: '/main/sequences',
        module: 'sequence',
      },
      {
        module: 'whatsapp_template',
        key: 'whatsapp_template',
        label: 'WhatsApp Templates',
        icon: 'fab fa-whatsapp',
        route: '/main/configuration-whatsapp-template',
        permissionParents: ['setting', 'campaign'],
      },
      {
        module: 'email_template',
        key: 'email_template',
        label: 'Email Templates',
        icon: 'fas fa-envelope',
        route: '/main/configuration-email-template',
        permissionParents: ['setting', 'campaign'],
      },
      {
        module: 'contact_group',
        key: 'contact_group',
        label: 'Contact Groups',
        icon: 'fas fa-user-friends',
        route: '/main/contact-groups',
        permissionParents: ['setting', 'campaign'],
      },
    ],
  },

  /* ===================== AUTOMATION ===================== */
  {
    key: 'automation',
    label: 'Automation',
    icon: 'fas fa-project-diagram',
    children: [
      {
        key: 'workflow_automation',
        label: 'Workflow Automation',
        icon: 'fas fa-sitemap',
        route: '/main/workflow-automation',
        module: 'workflow_automation',
      },
    ],
  },

  /* ===================== CUSTOMER SERVICE ===================== */
  {
    key: 'customer_service',
    label: 'Customer Service',
    shortLabel: 'Service',
    icon: 'fas fa-headset',
    children: [
      {
        key: 'whatsapp-live-chat',
        label: 'WhatsApp Live Chat',
        icon: 'fab fa-whatsapp',
        route: '/main/whatsapp-live-chat',
        module: 'whatsapp-live-chat',
      },
      {
        key: 'web-live-chat',
        label: 'Web Live Chat',
        icon: 'fas fa-comments',
        route: '/main/web-live-chat',
        module: 'live_chat_support',
      },
      {
        key: 'tickets',
        label: 'Tickets',
        icon: 'fas fa-ticket-alt',
        route: '/main/manage-tickets',
        module: 'tickets',
      },
      {
        key: 'sales-mail-hub',
        label: 'Mail Hub',
        icon: 'fas fa-envelope',
        route: '/main/sales-mail-hub',
        module: 'sales-mail-hub',
      },
    ],
  },

  /* ===================== CUSTOMER INTELLIGENCE ===================== */
  {
    key: 'customer_intelligence',
    label: 'Customer Intelligence',
    shortLabel: 'Intel',
    icon: 'fas fa-chart-area',
    children: [
      customerIntelligenceSection(
        'ci_overview',
        'Overview',
        'fas fa-brain',
        'overview',
      ),
      customerIntelligenceSection(
        'ci_churn_retention',
        'Churn & Retention',
        'fas fa-shield-alt',
        'churn',
      ),
      customerIntelligenceSection(
        'ci_next_best_action',
        'Next Best Action',
        'fas fa-magic',
        'nba',
      ),
      customerIntelligenceSection(
        'ci_cohort_migration',
        'Cohort Migration',
        'fas fa-layer-group',
        'migration',
      ),
      customerIntelligenceSection(
        'ci_rfm_engagement',
        'RFM & Engagement',
        'fas fa-gem',
        'rfm',
      ),
      customerIntelligenceSection(
        'ci_spend_commerce',
        'Spend & Commerce',
        'fas fa-shopping-cart',
        'spend',
      ),
      customerIntelligenceSection(
        'ci_loyalty_sentiment',
        'Loyalty & Sentiment',
        'fas fa-crown',
        'loyalty',
      ),
      customerIntelligenceSection(
        'ci_audience_roster',
        'Audience Roster',
        'fas fa-users-cog',
        'roster',
      ),
    ],
  },

  /* ===================== AI BOT PLATFORM ===================== */
  {
    key: 'ai_bot_platform',
    label: 'AI Bot Platform',
    shortLabel: 'Bots',
    icon: 'fas fa-robot',
    children: [
      {
        key: 'bots',
        label: 'Bots',
        icon: 'fas fa-robot',
        route: '/main/bots',
        module: 'bots',
        permissionParents: AI_BOT_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'bot_builder',
        label: 'Bot Builder',
        icon: 'fas fa-puzzle-piece',
        route: '/main/bot-builder',
        module: 'bot_builder',
        permissionParents: AI_BOT_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'bot_deployments',
        label: 'Deployments',
        icon: 'fas fa-cloud-upload-alt',
        route: '/main/bot-deployments',
        module: 'bot_deployments',
        permissionParents: AI_BOT_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'bot_conversations',
        label: 'Bot Conversations',
        icon: 'fas fa-comments',
        route: '/main/bot-conversations',
        module: 'bot_conversations',
        permissionParents: AI_BOT_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'bot_analytics',
        label: 'Bot Analytics',
        icon: 'fas fa-chart-bar',
        route: '/main/bot-analytics',
        module: 'bot_analytics',
        permissionParents: AI_BOT_PERMISSION_PARENTS,
        allowParentModule: true,
      },
    ],
  },

  /* ===================== COMMERCE ===================== */
  {
    key: 'commerce',
    label: 'Commerce',
    icon: 'fas fa-store',
    children: [
      {
        module: 'product_configuration',
        key: 'product_configuration',
        label: 'Product Configuration',
        icon: 'fas fa-box',
        route: '/main/product-configuration',
        permissionParents: ['setting'],
      },
      {
        module: 'product_categories',
        key: 'product_categories',
        label: 'Product Categories',
        icon: 'fas fa-tags',
        route: '/main/product-categories',
        permissionParents: ['setting'],
      },
      {
        module: 'tax_configuration',
        key: 'tax_configuration',
        label: 'Tax Configuration',
        icon: 'fas fa-receipt',
        route: '/main/tax-configuration',
        permissionParents: ['setting'],
      },
      {
        key: 'woocommerce',
        label: 'E-commerce',
        icon: 'fas fa-shopping-bag',
        module: 'woocommerce',
        children: [
          {
            module: 'woo_products',
            key: 'woo_products',
            label: 'Products',
            icon: 'fas fa-box',
            route: '/main/woocommerce-inventory',
          },
          {
            module: 'woo_orders',
            key: 'woo_orders',
            label: 'Orders',
            icon: 'fas fa-receipt',
            route: '/main/woocommerce-orders',
          },
        ],
      },
    ],
  },

  /* ===================== PROJECT MANAGEMENT ===================== */
  {
    key: 'project_management',
    label: 'Project Management',
    shortLabel: 'Projects',
    icon: 'fas fa-folder-open',
    children: [
      {
        key: 'projects',
        label: 'Projects',
        icon: 'fas fa-folder-open',
        route: '/main/manage-projects',
        module: 'projects',
      },
    ],
  },

  /* ===================== MY DESK (Employee Self-Service) ===================== */

  {
    key: 'my_desk',
    label: 'My Desk',
    shortLabel: 'My Desk',
    icon: 'fas fa-desktop',
    module: 'my_desk',
    children: [
      {
        key: 'my_desk_dashboard',
        module: 'my_desk_dashboard',
        label: 'My Dashboard',
        icon: 'fas fa-home',
        route: '/main/my-desk/my-dashboard',
      },
      {
        key: 'my_desk_attendance',
        module: 'my_desk_attendance',
        label: 'Attendance',
        icon: 'fas fa-calendar-check',
        route: '/main/my-desk/attendance',
      },
      {
        key: 'my_desk_leaves',
        module: 'my_desk_leaves',
        label: 'Leaves',
        icon: 'fas fa-calendar-alt',
        route: '/main/my-desk/leaves',
      },
      {
        key: 'my_desk_assets',
        module: 'my_desk_assets',
        label: 'Assets',
        icon: 'fas fa-boxes',
        route: '/main/my-desk/assets',
      },
      {
        key: 'my_desk_expenses',
        module: 'my_desk_expenses',
        label: 'Expenses',
        icon: 'fas fa-receipt',
        route: '/main/my-desk/expenses',
      },
      {
        key: 'my_desk_payslips',
        module: 'my_desk_payslips',
        label: 'Payslips',
        icon: 'fas fa-file-invoice-dollar',
        route: '/main/my-desk/payslips',
      },
      {
        key: 'my_desk_holidays',
        module: 'my_desk_holidays',
        label: 'Holiday Calendar',
        icon: 'fas fa-calendar-day',
        route: '/main/my-desk/holidays',
      },
      {
        key: 'my_desk_timesheet',
        module: 'my_desk_timesheet',
        label: 'Timesheet',
        icon: 'fas fa-business-time',
        route: '/main/my-desk/timesheet',
      },
      {
        key: 'my_desk_documents',
        module: 'my_desk_documents',
        label: 'Documents',
        icon: 'fas fa-file-contract',
        route: '/main/my-desk/documents',
      },
    ],
  },

  /* ===================== HRM (Admin Only) ===================== */

  /* ===================== HRM ===================== */
  {
    key: 'hrm',
    label: 'HRM',
    shortLabel: 'HRM',
    icon: 'fas fa-id-card',
    module: 'hrm',
    children: [
      {
        key: 'hrm_hr_dashboard',
        module: 'hrm_hr_dashboard',
        label: 'HR Dashboard',
        icon: 'fas fa-tachometer-alt',
        route: '/main/hrm/hr-dashboard',
      },
      {
        key: 'hrm_employees',
        module: 'hrm_employees',
        label: 'Employees',
        icon: 'fas fa-users',
        route: '/main/hrm/employees',
      },
      {
        key: 'hrm_org_depts',
        module: 'hrm_org_depts',
        label: 'Departments',
        icon: 'fas fa-sitemap',
        route: '/main/hrm/organization/departments',
      },
      {
        key: 'hrm_org_desig',
        module: 'hrm_org_desig',
        label: 'Designations',
        icon: 'fas fa-medal',
        route: '/main/hrm/organization/designations',
      },
      {
        key: 'hrm_org_branches',
        module: 'hrm_org_branches',
        label: 'Branches',
        icon: 'fas fa-building',
        route: '/main/hrm/organization/branches',
      },
      {
        key: 'hrm_attendance',
        module: 'hrm_attendance',
        label: 'Attendance',
        icon: 'fas fa-calendar-check',
        route: '/main/hrm/attendance',
      },
      {
        key: 'hrm_leave',
        module: 'hrm_leave',
        label: 'Leave Management',
        icon: 'fas fa-calendar-alt',
        route: '/main/hrm/leave',
      },
      {
        key: 'hrm_holidays',
        module: 'hrm_holidays',
        label: 'Holiday Calendar',
        icon: 'fas fa-calendar-alt',
        route: '/main/hrm/holidays',
      },
      {
        key: 'hrm_payroll',
        module: 'hrm_payroll',
        label: 'Payroll',
        icon: 'fas fa-file-invoice-dollar',
        route: '/main/hrm/payroll',
      },
      {
        key: 'hrm_timesheet',
        module: 'hrm_timesheet',
        label: 'Timesheet',
        icon: 'fas fa-business-time',
        route: '/main/hrm/timesheet',
      },
      {
        key: 'hrm_expenses',
        module: 'hrm_expenses',
        label: 'Expense',
        icon: 'fas fa-receipt',
        route: '/main/hrm/expenses',
      },
      {
        key: 'hrm_assets',
        module: 'hrm_assets',
        label: 'Asset',
        icon: 'fas fa-boxes',
        route: '/main/hrm/assets',
      },
    ],
  },

  /* ===================== ANALYTICS ===================== */
  {
    key: 'analytics_group',
    label: 'Analytics',
    icon: 'fas fa-poll',
    children: [
      {
        key: 'report',
        label: 'Reports',
        icon: 'fas fa-chart-pie',
        module: 'report',
        children: [
          {
            key: 'report_crm',
            label: 'CRM Reports',
            icon: 'fas fa-address-card',
            module: 'report',
            children: [
              reportItem(
                'report_contacts',
                'Contacts Report',
                'fas fa-address-book',
                'contacts',
              ),
              reportItem(
                'report_leads',
                'Leads Report',
                'fas fa-envelope-open-text',
                'leads',
              ),
              reportItem(
                'report_accounts',
                'Accounts Report',
                'fas fa-building',
                'accounts',
              ),
              reportItem(
                'report_deals',
                'Deals Report',
                'fas fa-handshake',
                'deals',
              ),
            ],
          },
          {
            key: 'report_sales',
            label: 'Sales Reports',
            icon: 'fas fa-dollar-sign',
            module: 'report',
            children: [
              reportItem(
                'report_quotes',
                'Quotes Report',
                'fas fa-quote-left',
                'quotes',
              ),
              reportItem(
                'report_invoices',
                'Invoices Report',
                'fas fa-file-invoice',
                'invoices',
              ),
              reportItem(
                'report_sales_performance',
                'Sales Performance',
                'fas fa-trophy',
                'sales-performance',
              ),
              reportItem(
                'report_revenue',
                'Revenue Report',
                'fas fa-dollar-sign',
                'revenue',
              ),
              reportItem(
                'report_forecast',
                'Forecast Report',
                'fas fa-chart-line',
                'forecast',
              ),
            ],
          },
          {
            key: 'report_marketing',
            label: 'Marketing Reports',
            icon: 'fas fa-bullhorn',
            module: 'report',
            children: [
              reportItem(
                'report_campaign',
                'Campaign Report',
                'fas fa-bullhorn',
                'campaigns',
              ),
              reportItem(
                'report_sequence',
                'Sequence Report',
                'fas fa-link',
                'sequence',
              ),
              reportItem(
                'report_marketing_analytics',
                'Marketing Analytics',
                'fas fa-chart-line',
                'marketing-analytics',
              ),
            ],
          },
          {
            key: 'report_customer_service',
            label: 'Customer Service Reports',
            icon: 'fas fa-headset',
            module: 'report',
            children: [
              reportItem(
                'report_tickets',
                'Tickets Report',
                'fas fa-ticket-alt',
                'tickets',
              ),
              reportItem(
                'report_wa_live_chat',
                'WhatsApp Live Chat Report',
                'fab fa-whatsapp',
                'whatsapp-live-chat',
              ),
              reportItem(
                'report_web_live_chat',
                'Web Live Chat Report',
                'fas fa-comments',
                'web-live-chat',
              ),
              reportItem(
                'report_agent_performance',
                'Agent Performance Report',
                'fas fa-user-tie',
                'agent-performance',
              ),
              reportItem(
                'report_resolution',
                'Resolution Report',
                'fas fa-check-circle',
                'resolution',
              ),
              reportItem(
                'report_response_time',
                'Response Time Report',
                'fas fa-clock',
                'response-time',
              ),
            ],
          },
          {
            key: 'report_ci',
            label: 'Customer Intelligence Reports',
            icon: 'fas fa-chart-area',
            module: 'report',
            children: [
              reportItem(
                'report_ci_health',
                'Customer Health Report',
                'fas fa-heartbeat',
                'customer-health',
              ),
              reportItem(
                'report_ci_churn',
                'Churn & Retention Report',
                'fas fa-user-minus',
                'churn-retention',
              ),
              reportItem(
                'report_ci_rfm',
                'RFM Report',
                'fas fa-layer-group',
                'rfm',
              ),
              reportItem(
                'report_ci_loyalty',
                'Loyalty Report',
                'fas fa-heart',
                'loyalty',
              ),
              reportItem(
                'report_ci_sentiment',
                'Sentiment Report',
                'fas fa-smile',
                'sentiment',
              ),
              reportItem(
                'report_ci_segmentation',
                'Customer Segmentation Report',
                'fas fa-users',
                'segmentation',
              ),
            ],
          },
          {
            key: 'report_bots',
            label: 'AI Bot Reports',
            icon: 'fas fa-robot',
            module: 'report',
            children: [
              reportItem(
                'report_bot_performance',
                'Bot Performance Report',
                'fas fa-tachometer-alt',
                'bot-performance',
              ),
              reportItem(
                'report_bot_conversation',
                'Bot Conversation Report',
                'fas fa-comments',
                'bot-conversation',
              ),
              reportItem(
                'report_bot_resolution',
                'Bot Resolution Report',
                'fas fa-check-double',
                'bot-resolution',
              ),
              reportItem(
                'report_bot_handoff',
                'Bot → Agent Handoff Report',
                'fas fa-exchange-alt',
                'bot-handoff',
              ),
              reportItem(
                'report_bot_analytics',
                'Bot Analytics Report',
                'fas fa-chart-bar',
                'bot-analytics',
              ),
            ],
          },
          {
            key: 'report_commerce',
            label: 'Commerce Reports',
            icon: 'fas fa-store',
            module: 'report',
            children: [
              reportItem(
                'report_products',
                'Products Report',
                'fas fa-box',
                'products',
              ),
              reportItem(
                'report_orders',
                'Orders Report',
                'fas fa-receipt',
                'orders',
              ),
              reportItem(
                'report_revenue_product',
                'Revenue by Product',
                'fas fa-chart-pie',
                'revenue-by-product',
              ),
              reportItem(
                'report_payment',
                'Payment Report',
                'fas fa-credit-card',
                'payment',
              ),
            ],
          },
          {
            key: 'report_projects',
            label: 'Project Reports',
            icon: 'fas fa-folder-open',
            module: 'report',
            children: [
              reportItem(
                'report_project_performance',
                'Project Performance',
                'fas fa-chart-line',
                'project-performance',
              ),
              reportItem(
                'report_project_progress',
                'Project Progress',
                'fas fa-tasks',
                'project-progress',
              ),
              reportItem(
                'report_project_profitability',
                'Project Profitability',
                'fas fa-coins',
                'project-profitability',
              ),
              reportItem(
                'report_project_resource',
                'Project Resource Report',
                'fas fa-user-clock',
                'project-resource',
              ),
            ],
          },
          {
            key: 'report_hrm',
            label: 'HRM Reports',
            icon: 'fas fa-id-card',
            module: 'report',
            children: [
              reportItem(
                'report_employee',
                'Employee Report',
                'fas fa-users',
                'employee',
              ),
              reportItem(
                'report_attendance',
                'Attendance Report',
                'fas fa-calendar-check',
                'attendance',
              ),
              reportItem(
                'report_leave',
                'Leave Report',
                'fas fa-calendar-alt',
                'leave',
              ),
              reportItem(
                'report_department',
                'Department Report',
                'fas fa-sitemap',
                'department',
              ),
              reportItem(
                'report_employee_performance',
                'Employee Performance',
                'fas fa-star',
                'employee-performance',
              ),
            ],
          },
        ],
      },
    ],
  },

  /* ===================== INTEGRATIONS ===================== */
  {
    key: 'integrations',
    label: 'Integrations',
    icon: 'fas fa-plug',
    children: [
      {
        key: 'integrations_all',
        label: 'All Integrations',
        icon: 'fas fa-th-large',
        route: '/main/integrations',
        queryParams: { category: 'all' },
        module: 'integrations_all',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_conversational',
        label: 'Conversational Channels',
        icon: 'fas fa-comments',
        route: '/main/integrations',
        queryParams: { category: 'conversational' },
        module: 'integrations_conversational',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_marketing',
        label: 'Marketing Automation',
        icon: 'fas fa-bullhorn',
        route: '/main/integrations',
        queryParams: { category: 'marketing' },
        module: 'integrations_marketing',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_contact_center',
        label: 'Contact Centre',
        icon: 'fas fa-headset',
        route: '/main/integrations',
        queryParams: { category: 'contact_center' },
        module: 'integrations_contact_center',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_crm',
        label: 'CRM Systems',
        icon: 'fas fa-address-card',
        route: '/main/integrations',
        queryParams: { category: 'crm' },
        module: 'integrations_crm',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_payments',
        label: 'Payments & Gateways',
        icon: 'fas fa-credit-card',
        route: '/main/integrations',
        queryParams: { category: 'payments' },
        module: 'integrations_payments',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_ecommerce',
        label: 'E-Commerce',
        icon: 'fas fa-shopping-cart',
        route: '/main/integrations',
        queryParams: { category: 'ecommerce' },
        module: 'integrations_ecommerce',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_forms',
        label: 'Forms & Capture',
        icon: 'fas fa-list-alt',
        route: '/main/integrations',
        queryParams: { category: 'forms' },
        module: 'integrations_forms',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_llm',
        label: 'AI & LLM Engines',
        icon: 'fas fa-brain',
        route: '/main/integrations',
        queryParams: { category: 'llm' },
        module: 'integrations_llm',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
      {
        key: 'integrations_live_chat',
        label: 'Live Chat',
        icon: 'fas fa-comment-dots',
        route: '/main/integrations',
        queryParams: { category: 'live_chat' },
        module: 'integrations_live_chat',
        permissionParents: INTEGRATION_PERMISSION_PARENTS,
        allowParentModule: true,
      },
    ],
  },

  /* ===================== AUDIT ===================== */
  {
    key: 'audit',
    label: 'Audit',
    icon: 'fas fa-clipboard-list',
    children: [
      {
        module: 'user_logs',
        key: 'user_logs',
        label: 'User Logs',
        icon: 'fas fa-users',
        route: '/main/user-logs',
        permissionParents: ['setting'],
      },
    ],
  },

  /* ===================== SETTINGS ===================== */
  {
    key: 'setting',
    label: 'Settings',
    shortLabel: 'Settings',
    icon: 'fas fa-cog',
    module: 'setting',
    children: [
      {
        key: 'admin_users_access',
        label: 'Users & Access',
        icon: 'fas fa-user-shield',
        children: [
          {
            module: 'user_setting',
            key: 'user_setting',
            label: 'System Users',
            icon: 'fas fa-user-cog',
            route: '/main/user-settings',
          },
          {
            module: 'role_based_access_control',
            key: 'role_based_access_control',
            label: 'Role Based Access Control',
            icon: 'fas fa-users',
            route: '/main/role-based-access-control',
          },
        ],
      },
      {
        key: 'admin_organization',
        label: 'Organization',
        icon: 'fas fa-sitemap',
        children: [
          {
            module: 'manage_bussiness_info',
            key: 'manage_bussiness_info',
            label: 'Manage Business Info',
            icon: 'fas fa-building',
            route: '/main/manage-bussiness-info',
          },
          {
            module: 'manage_centers',
            key: 'manage_centers',
            label: 'Manage Centers',
            icon: 'fas fa-building',
            route: '/main/manage-centers',
          },
          {
            module: 'manage_departments',
            key: 'manage_departments',
            label: 'Manage Departments',
            icon: 'fas fa-building',
            route: '/main/manage-departments',
          },
          {
            module: 'manage_teams',
            key: 'manage_teams',
            label: 'Manage Teams',
            icon: 'fas fa-user-friends',
            route: '/main/manage-teams',
          },
        ],
      },
      {
        key: 'admin_crm_config',
        label: 'CRM Configuration',
        icon: 'fas fa-sliders-h',
        children: [
          {
            module: 'contact_field_setting',
            key: 'contact_field_setting',
            label: 'Contact Field Setting',
            icon: 'fas fa-address-book',
            route: '/main/configuration-contact-fields',
          },
          {
            module: 'leads_field_setting',
            key: 'leads_field_setting',
            label: 'Leads Field Setting',
            icon: 'fas fa-edit',
            route: '/main/configuration-leads',
          },
          {
            module: 'account_field_setting',
            key: 'account_field_setting',
            label: 'Account Field Setting',
            icon: 'fas fa-building',
            route: '/main/configuration-account-fields',
          },
          {
            module: 'pipeline_setting',
            key: 'pipeline_setting',
            label: 'Pipeline Configuration',
            icon: 'fas fa-project-diagram',
            route: '/main/pipeline',
          },
        ],
      },
      {
        key: 'admin_campaign_config',
        label: 'Campaign Configuration',
        icon: 'fas fa-bullhorn',
        children: [
          {
            module: 'campaign',
            key: 'campaign_segment',
            label: 'Campaign Segments',
            icon: 'fas fa-layer-group',
            route: '/main/campaign-segments',
            permissionParents: ['campaign'],
          },
        ],
      },
      {
        key: 'admin_communication',
        label: 'Communication',
        icon: 'fas fa-comments',
        children: [
          {
            module: 'communication_setting',
            key: 'communication_setting',
            label: 'Communication Setting',
            icon: 'fas fa-comments',
            route: '/main/client-settings',
          },
          {
            module: 'domain_authentication',
            key: 'domain_authentication',
            label: 'Domain Authentication',
            icon: 'fas fa-shield-alt',
            route: '/main/domain-authentication',
          },
        ],
      },
      {
        key: 'admin_ai_voice',
        label: 'AI / Voice',
        icon: 'fas fa-microphone',
        children: [
          {
            module: 'ai_agent_flow',
            key: 'ai_agent_flow',
            label: 'Call Agent Flow',
            icon: 'fas fa-robot',
            route: '/main/ai-agent-flows',
          },
        ],
      },
    ],
  },

  /* ===================== BILLING & SUBSCRIPTION ===================== */
  {
    key: 'billing_portal',
    label: 'Billing & Subscription',
    shortLabel: 'Billing',
    icon: 'fas fa-file-invoice-dollar',
    module: 'billing_subscription',
    children: [
      {
        key: 'billing_overview',
        label: 'Overview',
        icon: 'fas fa-th-large',
        route: '/main/billing-subscription',
        module: 'billing_subscription',
      },
      {
        key: 'billing_subscription_group',
        label: 'Subscription',
        icon: 'fas fa-sync-alt',
        children: [
          billingItem(
            'billing_sub_current_plan',
            'Current Plan',
            'fas fa-star',
            'subscription',
          ),
          billingItem(
            'billing_upgrade',
            'Upgrade Plan',
            'fas fa-arrow-up',
            'subscription',
          ),
          billingItem(
            'billing_downgrade',
            'Downgrade Plan',
            'fas fa-arrow-down',
            'subscription',
          ),
          billingItem(
            'billing_cycle',
            'Change Billing Cycle',
            'fas fa-redo',
            'subscription',
          ),
          billingItem(
            'billing_cancel',
            'Cancel Subscription',
            'fas fa-times-circle',
            'subscription',
          ),
        ],
      },
      {
        key: 'billing_payment',
        label: 'Payment',
        icon: 'fas fa-credit-card',
        children: [
          billingItem(
            'billing_payment_method',
            'Payment Method',
            'fas fa-credit-card',
            'subscription',
          ),
          billingItem(
            'billing_add_card',
            'Add Card',
            'fas fa-plus-circle',
            'subscription',
          ),
          billingItem(
            'billing_update_card',
            'Update Card',
            'fas fa-pen',
            'subscription',
          ),
          billingItem(
            'billing_contact',
            'Billing Contact',
            'fas fa-address-card',
            'subscription',
          ),
        ],
      },
      {
        key: 'billing_invoices',
        label: 'Invoices',
        icon: 'fas fa-file-invoice',
        children: [
          billingItem(
            'billing_invoice_history',
            'Invoice History',
            'fas fa-history',
            'invoices',
          ),
        ],
      },
      {
        key: 'billing_settings',
        label: 'Billing Settings',
        icon: 'fas fa-cog',
        children: [
          billingItem(
            'billing_company_info',
            'Company Billing Information',
            'fas fa-building',
            'subscription',
          ),
          billingItem(
            'billing_tax_gst',
            'Tax / GST Information',
            'fas fa-receipt',
            'subscription',
          ),
          billingItem(
            'billing_email',
            'Billing Email',
            'fas fa-envelope',
            'subscription',
          ),
          billingItem(
            'billing_auto_pay',
            'Automatic Payment',
            'fas fa-sync-alt',
            'subscription',
          ),
        ],
      },
    ],
  },
];

export const MODULE_LOADING_MESSAGES: Record<string, string[]> = {
  accounts: [
    'Retrieving business accounts and portfolios...',
    'Syncing account relationships and mapping...',
    'Aggregating organizational account metrics...',
    'Updating business info and custom fields...',
  ],
  leads: [
    'Analyzing incoming leads and traffic sources...',
    'Scoring leads using engagement metrics...',
    'Assigning leads to representative owners...',
    'Checking lead status and stage changes...',
    'Updating contact information and custom fields...',
  ],
  deals: [
    'Aggregating pipeline value and deal sizes...',
    'Calculating win probabilities and deal stages...',
    'Checking pipeline forecast for the current quarter...',
    'Syncing active deal negotiations...',
    'Updating deal owner details...',
  ],
  contacts: [
    'Retrieving contact cards and profiles...',
    'Syncing contact groups and tags...',
    'Updating interaction history...',
    'Re-indexing email and call touchpoints...',
  ],
  invoices: [
    'Reconciling billing history and ledger balances...',
    'Processing recurring invoice statuses...',
    'Calculating taxes and line-item totals...',
    'Syncing gateway transaction statuses...',
  ],
  'billing-subscription': [
    'Reconciling billing history and ledger balances...',
    'Processing recurring invoice statuses...',
    'Calculating taxes and line-item totals...',
    'Syncing gateway transaction statuses...',
  ],
  forecast: [
    'Crunching sales pipeline performance...',
    'Compiling revenue forecasts and targets...',
    'Generating sales trends and projection curves...',
    'Aggregating team achievement analytics...',
  ],
  quotes: [
    'Generating custom quote proposals...',
    'Applying dynamic discount algorithms...',
    'Validating line items and tax calculations...',
    'Updating quote statuses...',
  ],
  'product-configuration': [
    'Retrieving product catalog and descriptions...',
    'Syncing real-time inventory and pricing tiers...',
    'Checking category classifications...',
    'Validating product attributes...',
  ],
  'woocommerce-inventory': [
    'Retrieving product catalog and descriptions...',
    'Syncing real-time inventory and pricing tiers...',
    'Checking category classifications...',
    'Validating product attributes...',
  ],
  'manage-projects': [
    'Fetching project tracking boards...',
    'Syncing active task milestones...',
    'Recalculating task ownership and due dates...',
    'Updating team velocity and progress...',
  ],
  'user-logs': [
    'Fetching application audit trail logs...',
    'Syncing team activity timestamps...',
    'Re-indexing log event profiles...',
    'Validating session security tokens...',
  ],
  'manage-tickets': [
    'Retrieving support tickets and requests...',
    'Sorting tickets by priority level...',
    'Mapping tickets to service agents...',
    'Updating ticket status and history...',
    'Re-indexing customer support logs...',
  ],
  'configuration-leads': [
    'Retrieving custom lead fields and formats...',
    'Syncing lead input validation rules...',
    'Updating lead layout card settings...',
    'Validating database schema for lead records...',
  ],
  'configuration-contact-fields': [
    'Retrieving custom contact attributes...',
    'Syncing validation rules for phone and email...',
    'Updating contact visibility preferences...',
    'Configuring database mappings for contact cards...',
  ],
  'configuration-account-fields': [
    'Retrieving business account field parameters...',
    'Syncing account classification criteria...',
    'Updating organization structure configurations...',
    'Re-indexing account metadata keys...',
  ],
  'configuration-whatsapp-template': [
    'Retrieving approved WhatsApp templates...',
    'Validating template content placeholders...',
    'Syncing statuses with Meta Business API...',
    'Optimizing interactive template templates...',
  ],
  'configuration-email-template': [
    'Retrieving email layout configurations...',
    'Compiling dynamic email body placeholders...',
    'Optimizing responsive email designs...',
    'Testing email footer block variables...',
  ],
  'campaign-segments': [
    'Retrieving campaign segments...',
    'Syncing segment names for this account...',
  ],
  'user-settings': [
    'Retrieving member profiles and status keys...',
    'Syncing active workspace sessions...',
    'Verifying user authentication methods...',
    'Configuring user invitation parameters...',
  ],
  'role-based-access-control': [
    'Reconciling role permission structures...',
    'Mapping access controls to user groups...',
    'Verifying feature authorization policies...',
    'Updating active group security tokens...',
  ],
  'client-settings': [
    'Retrieving email and SMS channel details...',
    'Syncing gateway connection protocols...',
    'Testing API webhooks for incoming replies...',
    'Configuring default notification parameters...',
  ],
  'contact-groups': [
    'Retrieving dynamic contact lists and cohorts...',
    'Calculating sizes of contact groups...',
    'Syncing automation group filters...',
    'Re-indexing database keys for contact lists...',
  ],
  'manage-departments': [
    'Retrieving organization departments...',
    'Syncing department group emails...',
    'Mapping department managers...',
    'Updating active department statistics...',
  ],
  'manage-centers': [
    'Retrieving physical branches and centers...',
    'Syncing regional center time zones...',
    'Mapping center operations managers...',
    'Updating active center capacities...',
  ],
  'product-categories': [
    'Retrieving product catalog hierarchies...',
    'Syncing parent-child category relations...',
    'Updating catalog filter classifications...',
    'Re-indexing category code parameters...',
  ],
  'tax-configuration': [
    'Retrieving tax rate tables and rules...',
    'Syncing localized tax categories...',
    'Validating dynamic invoice tax formulas...',
    'Updating automatic tax calculation parameters...',
  ],
  'manage-teams': [
    'Retrieving active team lists and hierarchies...',
    'Syncing team member assignment rules...',
    'Updating team target and quota metrics...',
    'Configuring team leaderboard settings...',
  ],
  'domain-authentication': [
    'Checking TXT, CNAME, and MX records...',
    'Querying remote DNS servers for validation...',
    'Verifying DKIM and SPF security status...',
    'Updating domain SSL/TLS certificate indicators...',
  ],
  'manage-bussiness-info': [
    'Retrieving organization details and address...',
    'Syncing corporate profile logo file...',
    'Updating localization and default currency...',
    'Verifying fiscal year settings...',
  ],
  pipeline: [
    'Retrieving sales pipeline steps and flows...',
    'Syncing win probability thresholds...',
    'Re-indexing deal progression rules...',
    'Updating pipeline stage sorting orders...',
  ],
  'woocommerce-orders': [
    'Reconciling billing history and ledger balances...',
    'Processing recurring invoice statuses...',
    'Calculating taxes and line-item totals...',
    'Syncing gateway transaction statuses...',
  ],
  home: [
    'Loading workspace overview...',
    'Aggregating dashboard metrics...',
    'Syncing recent activity...',
    'Preparing your home view...',
  ],
  generic: [
    'Analyzing database queries...',
    'Securing server connection...',
    'Fetching latest changes...',
    'Optimizing workspace view...',
  ],
};
